import React from 'react'
import { notification } from '@edulastic/common'
import { roleuser } from '@edulastic/constants'
import adminRouteApi from '@edulastic/api/src/adminRoute'
import { getUserFromRedux } from '@edulastic/api/src/utils/API'
import {
  MFA_SEGMENT_ACTIONS,
  trackMfaEvent,
  trackMfaSendFailureFromApiError,
} from '../../../../../student/Login/mfaSegmentTracking'
import { getMfaApiErrorKey } from '../../../../../student/Login/mfaErrorMessages'

export const MFA_CODE_ACTION_KEY = 'generateMfaCode'

/** authorUi loading key; matches data-cy on the menu item. */
export const GENERATE_MFA_CODE_LOADING_COMPONENT = 'generateMfaCodeAction'

export const selectIsGenerateMfaCodeLoading = (state) =>
  (state?.authorUi?.currentlyLoading || []).includes(
    GENERATE_MFA_CODE_LOADING_COMPONENT
  )

export const getGenerateMfaCodeRequestLoadingProps = ({
  isGenerateMfaCodeLoading,
  addLoadingComponent,
  removeLoadingComponent,
}) => ({
  isLoading: isGenerateMfaCodeLoading,
  onLoadingStart: () =>
    addLoadingComponent({
      componentName: GENERATE_MFA_CODE_LOADING_COMPONENT,
    }),
  onLoadingEnd: () =>
    removeLoadingComponent({
      componentName: GENERATE_MFA_CODE_LOADING_COMPONENT,
    }),
})

/** fflip `isMfaEnabled`: DA/SA and (global MFA_ENABLED env or district mfa-enabled). */
export const isMfaGenerateCodeFeatureEnabled = (features = {}) =>
  Boolean(features.isMfaEnabled)

export const showGenerateMfaCodeAction = (userRole, features = {}) => {
  const isDistrictOrSchoolAdmin =
    userRole === roleuser.DISTRICT_ADMIN || userRole === roleuser.SCHOOL_ADMIN
  return isDistrictOrSchoolAdmin && isMfaGenerateCodeFeatureEnabled(features)
}

const getMfaCodeErrorMessage = (err, t) => {
  const message =
    err?.response?.data?.message || err?.message || err?.data?.message || ''
  const errorMap = {
    permission_denied: t('users.mfaCode.errors.permissionDenied'),
    user_does_not_belong_to_your_organization: t(
      'users.mfaCode.errors.outOfOrganization'
    ),
    mfa_admin_code_not_allowed_for_student: t(
      'users.mfaCode.errors.studentNotAllowed'
    ),
    too_many_admin_generate_attempts: t('users.mfaCode.errors.tooManyAttempts'),
    user_not_found: t('users.mfaCode.errors.userNotFound'),
    no_active_mfa_session: t('users.mfaCode.errors.noActiveSession'),
  }
  return errorMap[message] || t('users.mfaCode.errors.generic')
}

let generateMfaCodeRequestInFlight = false

const trackAdminMfaCodeGenerated = () => {
  const role = getUserFromRedux()?.user?.role
  if (role === roleuser.DISTRICT_ADMIN) {
    trackMfaEvent(MFA_SEGMENT_ACTIONS.DA_GENERATED)
  } else if (role === roleuser.SCHOOL_ADMIN) {
    trackMfaEvent(MFA_SEGMENT_ACTIONS.SA_GENERATED)
  }
}

export const requestGenerateMfaCode = async ({
  userId,
  selectedUserEmail,
  t,
  onSuccess,
  onNoActiveSession,
  isLoading,
  onLoadingStart,
  onLoadingEnd,
}) => {
  if (isLoading || generateMfaCodeRequestInFlight) {
    return
  }

  generateMfaCodeRequestInFlight = true
  onLoadingStart?.()
  try {
    trackAdminMfaCodeGenerated()
    const result = await adminRouteApi.generateMfaCode({ userId })
    onSuccess(result, selectedUserEmail)
  } catch (err) {
    trackMfaSendFailureFromApiError(err)
    const errorKey = getMfaApiErrorKey(err)
    if (errorKey === 'no_active_mfa_session' && onNoActiveSession) {
      onNoActiveSession({ userEmail: selectedUserEmail || '' })
      return
    }
    notification({
      type: 'error',
      msg: getMfaCodeErrorMessage(err, t),
    })
  } finally {
    generateMfaCodeRequestInFlight = false
    onLoadingEnd?.()
  }
}

export const handleGenerateMfaCodeSelection = ({
  selectedRowKeys,
  selectedUserEmail,
  t,
  onSuccess,
  onNoActiveSession,
  isLoading,
  onLoadingStart,
  onLoadingEnd,
}) => {
  if (isLoading) {
    return
  }
  if (selectedRowKeys.length === 0) {
    notification({ messageKey: 'pleaseSelectUser' })
    return
  }
  if (selectedRowKeys.length > 1) {
    notification({ msg: t('users.mfaCode.selectSingleUser') })
    return
  }

  requestGenerateMfaCode({
    userId: selectedRowKeys[0],
    selectedUserEmail,
    t,
    onSuccess,
    onNoActiveSession,
    isLoading,
    onLoadingStart,
    onLoadingEnd,
  })
}

export const renderGenerateMfaCodeMenuItem = ({
  show,
  loading,
  handleGlobalKeyDown,
  t,
  MenuItem,
}) => {
  if (!show) {
    return null
  }

  return (
    <MenuItem
      onKeyDown={handleGlobalKeyDown}
      key={MFA_CODE_ACTION_KEY}
      role="menuitem"
      tabIndex={-1}
      disabled={loading}
      data-cy="generateMfaCodeAction"
    >
      {loading ? t('users.mfaCode.generating') : t('users.mfaCode.actionLabel')}
    </MenuItem>
  )
}

export const appendMfaCodeActionToLabelsMap = ({ labelsMap, t, show }) => {
  if (!show) {
    return labelsMap
  }
  return {
    ...labelsMap,
    [t('users.mfaCode.actionLabel')]: MFA_CODE_ACTION_KEY,
  }
}

export const getInitialMfaCodeModalState = () => ({
  generateMfaCodeModalVisible: false,
  generateMfaCode: '',
  generateMfaCodeExpiresInMinutes: 10,
  generateMfaCodeMaskedEmail: '',
  generateMfaCodeNoActiveSession: false,
  generateMfaCodeUserEmail: '',
})

export const getSelectedUserEmail = (adminUsersData, userId) => {
  const user = adminUsersData?.[userId]?._source
  if (!user) {
    return ''
  }
  return user.email || user.username || ''
}

export const openMfaCodeModalFromResult = (result, userEmail = '') => ({
  ...getInitialMfaCodeModalState(),
  generateMfaCodeModalVisible: true,
  generateMfaCode: result?.code || '',
  generateMfaCodeExpiresInMinutes: result?.expiresInMinutes || 10,
  generateMfaCodeMaskedEmail: result?.maskedEmail || '',
  generateMfaCodeUserEmail: userEmail,
})

export const openMfaCodeModalNoActiveSession = ({ userEmail } = {}) => ({
  ...getInitialMfaCodeModalState(),
  generateMfaCodeModalVisible: true,
  generateMfaCodeNoActiveSession: true,
  generateMfaCodeUserEmail: userEmail || '',
})

export const hideMfaCodeModalState = () => ({
  generateMfaCodeModalVisible: false,
})

export const resetMfaCodeModalState = () => getInitialMfaCodeModalState()
